/**
 * Normalizes CMS section JSON for the public site: items_json → items,
 * safe bilingual merges (text from AR, media/URLs from EN).
 */

import type { CardsSectionData, CardItem } from "@/components/sections/CardsSection";
import type { HeroData } from "@/components/sections/HeroSection";
import type { ImageSectionData } from "@/components/sections/ImageSection";
import type { ImageTextRowData } from "@/components/sections/ImageTextRowSection";
import { normalizeImageTextRowData } from "@/lib/image-text-row";
import type { VideoSectionData } from "@/components/sections/VideoSection";

export { normalizeImageTextRowData };

export function strField(v: unknown): string {
  if (v == null) return "";
  return String(v).trim();
}

/** Parse items from API `items` array or `items_json` string. */
export function parseSectionItems(raw: unknown): unknown[] {
  if (Array.isArray(raw)) return raw;
  if (typeof raw === "string" && raw.trim()) {
    try {
      const parsed: unknown = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed;
      if (typeof parsed === "string") {
        const again: unknown = JSON.parse(parsed);
        if (Array.isArray(again)) return again;
      }
    } catch {
      // ignore
    }
  }
  return [];
}

function pickBilingual(
  key: string,
  data: Record<string, unknown>,
  dataAr: Record<string, unknown> | undefined,
  lang: string | undefined,
): string | undefined {
  const useAr = lang === "ar" && dataAr;
  if (useAr && dataAr) {
    const ar = dataAr[key];
    if (ar != null && strField(ar) !== "") return strField(ar);
  }
  const en = data[key];
  return en != null ? strField(en) : undefined;
}

/** Media / URL fields always come from the primary (English) payload. */
function pickMedia(key: string, data: Record<string, unknown>): string | undefined {
  const v = data[key];
  return v != null && strField(v) !== "" ? strField(v) : undefined;
}

export function normalizeHeroData(
  data: Record<string, unknown>,
  dataAr?: Record<string, unknown>,
  lang?: string,
): HeroData {
  return {
    title: pickBilingual("title", data, dataAr, lang),
    subtitle: pickBilingual("subtitle", data, dataAr, lang),
    badge_text: pickBilingual("badge_text", data, dataAr, lang),
    cta_primary_label: pickBilingual("cta_primary_label", data, dataAr, lang),
    cta_primary_url: pickMedia("cta_primary_url", data),
    cta_secondary_label: pickBilingual("cta_secondary_label", data, dataAr, lang),
    cta_secondary_url: pickMedia("cta_secondary_url", data),
    background_type: pickMedia("background_type", data) || "image",
    bg_image_url: pickMedia("bg_image_url", data) || pickMedia("bgImageUrl", data),
    overlay_opacity: data.overlay_opacity as HeroData["overlay_opacity"],
    theme: pickMedia("theme", data),
    video_url: pickMedia("video_url", data),
    poster_url: pickMedia("poster_url", data),
    provider: pickMedia("provider", data),
  };
}

export function normalizeImageSectionData(
  data: Record<string, unknown>,
  dataAr?: Record<string, unknown>,
  lang?: string,
): ImageSectionData {
  return {
    image_url: pickMedia("image_url", data) || pickMedia("imageUrl", data),
    image_alt: pickMedia("image_alt", data) || pickMedia("imageAlt", data),
    caption: pickBilingual("caption", data, dataAr, lang),
    align: (pickMedia("align", data) || "center") as ImageSectionData["align"],
  };
}

export function normalizeVideoSectionData(
  data: Record<string, unknown>,
  dataAr?: Record<string, unknown>,
  lang?: string,
): VideoSectionData {
  return {
    provider: pickMedia("provider", data),
    video_url: pickMedia("video_url", data),
    poster_url: pickMedia("poster_url", data),
    heading: pickBilingual("heading", data, dataAr, lang),
    caption: pickBilingual("caption", data, dataAr, lang),
    aspect: (pickMedia("aspect", data) || "16:9") as VideoSectionData["aspect"],
  };
}

type TextData = {
  eyebrow?: string;
  heading?: string;
  subheading?: string;
  content?: string;
  layout?: "centered" | "wide" | "prose";
};

export function normalizeTextSectionData(
  data: Record<string, unknown>,
  dataAr?: Record<string, unknown>,
  lang?: string,
): TextData {
  return {
    eyebrow: pickBilingual("eyebrow", data, dataAr, lang),
    heading: pickBilingual("heading", data, dataAr, lang),
    subheading: pickBilingual("subheading", data, dataAr, lang),
    content: pickBilingual("content", data, dataAr, lang),
    layout: (pickMedia("layout", data) || "prose") as TextData["layout"],
  };
}

type CTAData = {
  heading?: string;
  subheading?: string;
  button_label?: string;
  button_url?: string;
  style?: "dark" | "light" | "accent";
};

export function normalizeCTASectionData(
  data: Record<string, unknown>,
  dataAr?: Record<string, unknown>,
  lang?: string,
): CTAData {
  return {
    heading: pickBilingual("heading", data, dataAr, lang),
    subheading: pickBilingual("subheading", data, dataAr, lang),
    button_label: pickBilingual("button_label", data, dataAr, lang),
    button_url: pickMedia("button_url", data),
    style: (pickMedia("style", data) || "dark") as CTAData["style"],
  };
}

function cardFromUnknown(v: unknown): CardItem | null {
  if (!v || typeof v !== "object") return null;
  const o = v as Record<string, unknown>;
  const image_url = strField(o.image_url ?? o.imageUrl);
  const title = strField(o.title);
  const body = strField(o.body ?? o.description);
  const icon = strField(o.icon);
  const cta_label = strField(o.cta_label ?? o.ctaLabel);
  const cta_url = strField(o.cta_url ?? o.ctaUrl);
  if (!title && !body && !image_url && !icon && !cta_label) return null;
  return { image_url, title, body, icon, cta_label, cta_url };
}

function mergeCardItems(en: CardItem[], ar: CardItem[]): CardItem[] {
  if (ar.length === 0) return en;
  return en.map((item, i) => {
    const tr = ar[i];
    if (!tr) return item;
    return {
      image_url: tr.image_url?.trim() || item.image_url,
      icon: tr.icon?.trim() || item.icon,
      title: tr.title?.trim() || item.title,
      body: tr.body?.trim() || item.body,
      cta_label: tr.cta_label?.trim() || item.cta_label,
      cta_url: tr.cta_url?.trim() || item.cta_url,
    };
  });
}

export function normalizeCardsSectionData(
  data: Record<string, unknown>,
  dataAr?: Record<string, unknown>,
  lang?: string,
): CardsSectionData {
  const enRaw = parseSectionItems(data.items ?? data.items_json);
  const enItems = enRaw
    .map(cardFromUnknown)
    .filter((x): x is CardItem => x !== null);

  const useAr = lang === "ar" && dataAr;
  const arItems = useAr
    ? parseSectionItems(dataAr!.items ?? dataAr!.items_json)
        .map(cardFromUnknown)
        .filter((x): x is CardItem => x !== null)
    : [];

  const cols = data.columns;
  return {
    eyebrow: pickBilingual("eyebrow", data, dataAr, lang),
    heading: pickBilingual("heading", data, dataAr, lang),
    subheading: pickBilingual("subheading", data, dataAr, lang),
    columns: cols != null ? String(cols) : "3",
    items: useAr ? mergeCardItems(enItems, arItems) : enItems,
  };
}

type FeatureItem = { icon?: string; title: string; description: string };

function featureFromUnknown(v: unknown): FeatureItem | null {
  if (!v || typeof v !== "object") return null;
  const o = v as Record<string, unknown>;
  const title = strField(o.title);
  const description = strField(o.description ?? o.body);
  const icon = strField(o.icon);
  if (!title && !description && !icon) return null;
  return { icon: icon || undefined, title: title || " ", description };
}

function mergeFeatureItems(en: FeatureItem[], ar: FeatureItem[]): FeatureItem[] {
  if (ar.length === 0) return en;
  return en.map((item, i) => {
    const tr = ar[i];
    if (!tr) return item;
    return {
      icon: tr.icon?.trim() || item.icon,
      title: tr.title?.trim() || item.title,
      description: tr.description?.trim() || item.description,
    };
  });
}

export function normalizeFeatureGridSectionData(
  data: Record<string, unknown>,
  dataAr?: Record<string, unknown>,
  lang?: string,
): {
  eyebrow?: string;
  heading?: string;
  subheading?: string;
  columns?: number;
  items?: FeatureItem[];
} {
  const enRaw = parseSectionItems(data.items ?? data.items_json);
  const enItems = enRaw
    .map(featureFromUnknown)
    .filter((x): x is FeatureItem => x !== null);

  const useAr = lang === "ar" && dataAr;
  const arItems = useAr
    ? parseSectionItems(dataAr!.items ?? dataAr!.items_json)
        .map(featureFromUnknown)
        .filter((x): x is FeatureItem => x !== null)
    : [];

  const cols = parseInt(strField(data.columns) || "3", 10);
  return {
    eyebrow: pickBilingual("eyebrow", data, dataAr, lang),
    heading: pickBilingual("heading", data, dataAr, lang),
    subheading: pickBilingual("subheading", data, dataAr, lang),
    columns: cols === 2 || cols === 4 ? cols : 3,
    items: useAr ? mergeFeatureItems(enItems, arItems) : enItems,
  };
}

type FAQItem = { question: string; answer: string };

function faqFromUnknown(v: unknown): FAQItem | null {
  if (!v || typeof v !== "object") return null;
  const o = v as Record<string, unknown>;
  const question = strField(o.question);
  const answer = strField(o.answer ?? o.body);
  if (!question && !answer) return null;
  return { question: question || " ", answer };
}

function mergeFAQItems(en: FAQItem[], ar: FAQItem[]): FAQItem[] {
  if (ar.length === 0) return en;
  return en.map((item, i) => {
    const tr = ar[i];
    if (!tr) return item;
    return {
      question: tr.question?.trim() || item.question,
      answer: tr.answer?.trim() || item.answer,
    };
  });
}

export function normalizeFAQSectionData(
  data: Record<string, unknown>,
  dataAr?: Record<string, unknown>,
  lang?: string,
): { heading?: string; subheading?: string; items?: FAQItem[] } {
  const enRaw = parseSectionItems(data.items ?? data.items_json);
  const enItems = enRaw
    .map(faqFromUnknown)
    .filter((x): x is FAQItem => x !== null);

  const useAr = lang === "ar" && dataAr;
  const arItems = useAr
    ? parseSectionItems(dataAr!.items ?? dataAr!.items_json)
        .map(faqFromUnknown)
        .filter((x): x is FAQItem => x !== null)
    : [];

  return {
    heading: pickBilingual("heading", data, dataAr, lang),
    subheading: pickBilingual("subheading", data, dataAr, lang),
    items: useAr ? mergeFAQItems(enItems, arItems) : enItems,
  };
}

export type { ImageTextRowData };
